import { Component, OnInit, Input, ElementRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, FormControl, FormArray, Validators } from '@angular/forms';

import { Lead } from '../../../../../shared/lead';
import { PostLead } from '../../../../../shared/post-lead';
import type { FormResponse } from '../../../../../shared/form-response';
import { leadValues } from '../../../../../services/lead-values.service';
import { FruHttpService } from '../../../../../services/fru-http.service';
import { JornayaFormService, JornayaNotification } from '../../../../../services/jornaya-form.service';
import { ConsentAttemptTelemetryService, ConsentAttemptEventOptions } from '../../../../../services/consent-attempt-telemetry.service';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'consent-form',
    templateUrl: './consent-form.component.html',
    styleUrls: ['./consent-form.component.scss'],
    animations: [
        trigger('fadeAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':leave', [
                animate('0.5s ease-out', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})

export class ConsentFormComponent implements OnInit {
  private static readonly EMAIL_PATTERN = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  private _availableSelected = {
    lead_id: 0,
    max_prompt: 0,
    status: '',
    lenders: []
  };
  @Input() lead: Lead;
  @Input() selectedEnabled: boolean;
  @Input() encryptedLeadId: string;
  // @Input() availableSelected;
  @Input() set availableSelected(_availableSelected) {
    this._availableSelected = _availableSelected;
  }

  public showPrompt: boolean = false;
  public promptAsked: boolean = false;
  public promptAllowed: boolean = false;
  public isPromptFinished: boolean = false;
  public promptDiplayedLender;
  public promptStep: number = 0;

  public leadForm: UntypedFormGroup;
  public leadValues: Object;
  public phoneMask: Object = {
    mask: '(#00) 000-0000',
    definitions: {'#': /[1-9]/},
    lazy: false
  }
  public zipMask: Object = {
    mask: '00000', 
    lazy: false
  }
  public disableSubmit: boolean = false;
  public buyingProcessControl;
  public veteranMilitaryControl;
  public zipCodeControl;
  public firstNameControl;
  public lastNameControl;
  public emailControl;
  public phoneControl;
  public notifications: JornayaNotification[] = [];
  public consentSource: string = '';
  public consentSubmissionId: string = '';
  public serverEmailError: string = '';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private fruHttp: FruHttpService,
    private elRef:ElementRef,
    private route: ActivatedRoute,
    private router: Router,
    private jornayaFormService: JornayaFormService,
    private consentTelemetry: ConsentAttemptTelemetryService,
  ) {
    this.leadValues = leadValues;
  }

  get availableSelected() {
    return this._availableSelected;
  }

  ngOnInit() {
    this.consentSource = this.getConsentSource();
    this.consentSubmissionId = this.createConsentSubmissionId();
    //FRU-22871: idempotent — restores the attempt the consent page already started for this
    //encrypted lead id (or creates one if the form is ever reached directly). Diagnostics only.
    this.consentTelemetry.startAttempt(this.encryptedLeadId, this.consentSource || 'web');
    this.buildForm();
  }

  //FRU-22871 diagnostics only. Every consent-form event carries the attempt id (via the
  //telemetry service) and this form's submission id. Telemetry failures never block the form:
  //ConsentAttemptTelemetryService.record swallows its own errors.
  private recordConsentEvent(event: ConsentAttemptEventOptions): void {
    this.consentTelemetry.record(this.encryptedLeadId, {
      ...event,
      submissionId: event.submissionId || this.consentSubmissionId || undefined,
    });
  }

  private recordConsentEventOnUnload(event: ConsentAttemptEventOptions): void {
    this.consentTelemetry.recordOnUnload(this.encryptedLeadId, {
      ...event,
      submissionId: event.submissionId || this.consentSubmissionId || undefined,
    });
  }

  buildForm() {
    // Adjust some numbers that might not exactly match the form dropdown
    let purchasePrice = this.getClosestValue(this.lead.purchasePrice, leadValues.purchasePrices);
    let cashOut = this.getClosestValue(this.lead.cashOut, leadValues.cashOuts);
    let propertyValue = this.getClosestValue(this.lead.propertyValue, leadValues.propertyValues);
    let firstMortgageBalance = this.getClosestValue(this.lead.firstMortgageBalance, leadValues.firstMortgageBalances);

    // Figure out what the buying process dropdown should be showing
    let buyingProcess = 'just_thinking';
    if (this.lead.signedAgreement === 'yes') {
      buyingProcess = 'signed_agreement';
    } else if (this.lead.foundHome === 'yes') {
      buyingProcess = 'found_home';
    } else if (this.lead.daysFromBuying === 'yes') {
      buyingProcess = 'days_from_buying';
    }

    // Set up form
    this.leadForm = this.formBuilder.group({
      encryptedLeadId: this.formBuilder.control(this.encryptedLeadId),
      rateType: this.formBuilder.control(this.lead.rateType),
      propertyType: this.formBuilder.control(this.lead.propertyType),
      propertyUse: this.formBuilder.control(this.lead.propertyUse),
      creditRating: this.formBuilder.control(this.lead.creditRating),
      zipCode: this.formBuilder.control(this.lead.zipCode), // , [Validators.required, Validators.pattern(/^(\d{5})$/)]
      veteranMilitary: this.formBuilder.control(this.lead.veteranMilitary),
      firstName: this.formBuilder.control(this.lead.firstName, [Validators.required, Validators.minLength(2)]),
      lastName: this.formBuilder.control(this.lead.lastName, [Validators.required, Validators.minLength(2)]),
      email: this.formBuilder.control(this.lead.email, [
        Validators.required,
        Validators.email,
        Validators.pattern(ConsentFormComponent.EMAIL_PATTERN),
      ]),
      phone: this.formBuilder.control(this.lead.phone, [Validators.required, Validators.pattern(/(\(\d{3}\) \d{3}-\d{4})|\d{10}/)])
    });

    this.leadForm.addControl('formVersion', new UntypedFormControl('consent-form'));
    this.leadForm.addControl('submissionId', new UntypedFormControl(this.consentSubmissionId));
    this.leadForm.addControl('emailTyped', new UntypedFormControl(1));

    // Type specific fields
    switch(this.lead.type) {
      case 'purchase':
        this.leadForm.addControl('purchasePrice', this.formBuilder.control(purchasePrice));
        this.leadForm.addControl('downPayment', this.formBuilder.control(this.lead.downPayment));
        this.leadForm.addControl('buyingProcess', this.formBuilder.control(buyingProcess));
        this.leadForm.addControl('daysFromBuying', this.formBuilder.control(this.lead.daysFromBuying));
        this.leadForm.addControl('foundHome', this.formBuilder.control(this.lead.foundHome));
        this.leadForm.addControl('signedAgreement', this.formBuilder.control(this.lead.signedAgreement));
        break;
      case 'refinance':
        this.leadForm.addControl('cashOut', this.formBuilder.control(cashOut));
        this.leadForm.addControl('propertyValue', this.formBuilder.control(propertyValue));
        this.leadForm.addControl('firstMortgageBalance', this.formBuilder.control(firstMortgageBalance));
        this.leadForm.addControl('currentFHA', this.formBuilder.control(this.lead.currentFHA));
        this.leadForm.addControl('currentVA', this.formBuilder.control(this.lead.currentVA));
        break;
    }

    // Controls for use in view
    this.buyingProcessControl = this.leadForm.get('buyingProcess');
    this.veteranMilitaryControl = this.leadForm.get('veteranMilitary');
    this.zipCodeControl = this.leadForm.get('zipCode');
    this.firstNameControl = this.leadForm.get('firstName');
    this.lastNameControl = this.leadForm.get('lastName');
    this.emailControl = this.leadForm.get('email');
    this.phoneControl = this.leadForm.get('phone');

    if (this.selectedEnabled) {
      this.leadForm.addControl('lendersList', new FormArray([]));
      this.leadForm.addControl('lenders', new FormArray([]));
      this.leadForm.addControl('lenders_prompt', new FormArray([]));
      this.leadForm.addControl('selected_tcpa', new UntypedFormControl('1'));
      this.leadForm.addControl('selectedListSource', new UntypedFormControl('selected_manager'));
    }
  }

  ngOnChanges() {
    if (this.availableSelected.lenders.length > 0) {

      if (this.availableSelected.status == 'default_angular_empty_response'
        || this.availableSelected.status == 'default_angular_no_response'
      ) {
        this.leadForm.controls.selectedListSource.setValue(this.availableSelected.status)
      }

      const lenderArray: FormArray = this.leadForm.get('lenders') as FormArray;
      const lenderArrayPrompt: FormArray = this.leadForm.get('lenders_prompt') as FormArray;
      const lenderList: FormArray = this.leadForm.get('lendersList') as FormArray;

      this.promptAllowed = true;

      this.availableSelected.lenders.forEach(value => {
        if (value.checked) {
          lenderArray.push(new UntypedFormControl(value?.id));
          lenderArrayPrompt.push(new UntypedFormControl(value?.id));
        }
        lenderList.push(new UntypedFormControl(value));
      });
    }
  }

  onSubmitForm() {
    if (this.disableSubmit) {
      return;
    }

    this.recordConsentEvent({ stage: 'submit_clicked', outcome: 'success' });

    if (!this.prepareEmailSubmission()) {
      return;
    }
    this.disableSubmit = true;
    this.dismissNotification();

    this.jornayaFormService.waitForTokenOrReport(this.encryptedLeadId).then(result => {
      if (!result.token) {
        this.recordConsentEvent({ stage: 'jornaya_missing', outcome: 'failure', reason: result.status || 'unknown' });
        this.handleJornayaTokenMissing(result.notification);
        return;
      }

      this.submitForm();
    });
  }

  handleJornayaTokenMissing(notification?: JornayaNotification) {
    if (!notification) {
      return;
    }

    this.notifications = [notification];
    this.disableSubmit = false;
  }

  submitForm() {
    if (!this.prepareEmailSubmission()) {
      return;
    }

    let checkedList = [];
    if (this.selectedEnabled) {
      // Check is all unchecked
      this.leadForm.value.lendersList.forEach(value => {
        if (value.checked) {
          checkedList.push(value.id);
        }
      });
    }

    let isAllDeSelected = false;
    if (this.selectedEnabled
      && this.leadForm.value.lendersList.length > 0
      && checkedList.length == 0
      // && this.availableSelected.max_prompt > 0
      && this.availableSelected.max_prompt > this.promptStep
    ) {
      isAllDeSelected = true

      this.initPrompt(1);
      this.showPrompt = true;
      this.promptAsked = true;

    } else {
      // Get injected elements from tracking scripts
      let trustedForm = this.elRef.nativeElement.querySelector('[name=xxTrustedFormCertUrl]');
      this.lead.trusted_form_cert_url = (trustedForm !== null) ? trustedForm.value : null;
      if (trustedForm === null || !trustedForm.value) {
        this.recordConsentEvent({
          stage: 'trustedform_missing',
          outcome: 'failure',
          reason: trustedForm === null ? 'cert_input_missing' : 'cert_value_missing',
        });
      }

      // const experimentStuff = this.experimentService.forPost();
      // this.lead.experiment = experimentStuff.experiment;
      // this.lead.experiment_variation = experimentStuff.variation;

      // Also guards the continueWith() -> submitForm() path, which bypasses onSubmitForm().
      const jornayaResult = this.jornayaFormService.getTokenOrReport(this.encryptedLeadId);
      this.lead.universal_leadid = jornayaResult.token;
      if (!this.lead.universal_leadid) {
        this.recordConsentEvent({ stage: 'jornaya_missing', outcome: 'failure', reason: jornayaResult.status || 'unknown' });
        this.handleJornayaTokenMissing(jornayaResult.notification);
        return;
      }

      // Assign form values
      this.lead = Object.assign(this.lead, this.leadForm.value);
      this.lead.consent_source = this.consentSource;

      // Strip things from phone
      this.lead.phone = this.lead.phone.replace(/[^\d]/g, '').slice(0, 10);



    // Submit the lead
    let postLead = new PostLead(this.lead);
    this.fruHttp.submitConsent(postLead)
        .subscribe({
          next: response => {
            this.handleResponse(response);
          },
          error: error => {
            this.recordConsentEvent({
              stage: 'submit_transport_failed',
              outcome: 'failure',
              reason: (error?.status === 0 || error?.status === undefined) ? 'network' : 'http_error',
              httpStatus: error?.status ?? 0,
            });
            this.handleSubmitFailure(
              error?.error?.message,
              error?.error?.consent_confirmation?.reason === 'invalid_email',
            );
          },
        });
    }
  }

  prepareEmailSubmission(): boolean {
    this.serverEmailError = '';
    const email = String(this.emailControl?.value || '').trim();
    this.emailControl?.setValue(email);
    this.emailControl?.markAsTouched();

    if (!this.consentSubmissionId || !this.emailControl || this.emailControl.invalid) {
      this.disableSubmit = false;
      this.recordConsentEvent({
        stage: 'validation_failed',
        outcome: 'failure',
        reason: !this.consentSubmissionId ? 'submission_id_missing' : 'local_validation',
        metadata: !this.consentSubmissionId ? {} : { field: 'email' },
      });
      if (!this.consentSubmissionId) {
        this.notifications = [{
          type: 'danger',
          message: 'We could not safely prepare this form. Please refresh the page and try again.',
        }];
      }
      return false;
    }

    this.leadForm.controls.submissionId.setValue(this.consentSubmissionId);
    this.leadForm.controls.emailTyped.setValue(1);
    return true;
  }

  createConsentSubmissionId(): string {
    const cryptoApi = globalThis.crypto;
    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
      return '';
    }

    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');

    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
  }

  getConsentSource(): string {
    const source = String(this.route.snapshot.queryParams.src || '').toLowerCase();

    return ['sms', 'email'].includes(source) ? source : '';
  }

  getClosestValue(goal: number, numbers: any[]) {
    // Assuming 'numbers' will be one of the key/value array of objects for form values
    let values = [];
    for (let k in numbers) {
      values.push(numbers[k].key);
    }

    let closest = values.reduce((prev, curr) => Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev);

    return closest;
  }

  handleResponse(response: FormResponse) {
    if (response.status === 'Refresh Required') {
      if (response.lead_id) {
        this.recordConsentEvent({
          stage: 'stale_link_redirect',
          outcome: 'unknown',
          reason: 'refresh_required',
          requestId: String(response.lead_id),
          originalRequestId: this.encryptedLeadId,
          metadata: { original_encrypted_lead_id: this.encryptedLeadId },
        });
        this.router.navigate(['/consent', response.lead_id], { queryParams: this.route.snapshot.queryParams });
        return;
      }
      this.recordConsentEventOnUnload({ stage: 'server_rejected', outcome: 'unknown', reason: 'refresh_required' });
      this.fruHttp.redirectOffers(this.lead);
      return;
    }

    const confirmation = response?.consent_confirmation;
    if (!confirmation
      || confirmation.accepted !== true
      || confirmation.email_confirmed !== true
    ) {
      this.recordConsentEvent({
        stage: 'server_rejected',
        outcome: 'failure',
        reason: confirmation?.reason || 'not_confirmed',
        httpStatus: 200,
      });
      this.handleSubmitFailure(response?.message, confirmation?.reason === 'invalid_email');
      return;
    }

    switch (response.status) {
      case 'New':
      case 'Sent':
      case 'Block':
      case 'Review':
      case 'Not Sendable':
        // Go to thank you page
        this.recordConsentEventOnUnload({ stage: 'server_accepted', outcome: 'success', httpStatus: 200 });
        this.fruHttp.redirectThankYou(this.lead, response);
        break;

      default:
        this.recordConsentEvent({
          stage: 'server_rejected',
          outcome: 'failure',
          reason: 'unhandled_status',
          metadata: { response_status: response.status },
        });
        this.handleSubmitFailure();
        break;
    }
  }

  handleSubmitFailure(message?: string, emailInvalid: boolean = false) {
    this.disableSubmit = false;
    if (emailInvalid) {
      this.serverEmailError = 'Please enter a valid email address.';
      this.emailControl?.setErrors({ ...(this.emailControl.errors || {}), server: true });
      this.emailControl?.markAsTouched();
    }
    this.notifications = [{
      type: 'danger',
      message: message || 'We could not confirm your information. Please review your email and try again.',
    }];
  }

  buyingProcessChange() {
    switch(this.buyingProcessControl.value) {
      case 'days_from_buying':
        this.leadForm.controls['daysFromBuying'].setValue('yes');
        break;
      case 'found_home':
        this.leadForm.controls['daysFromBuying'].setValue('yes');
        this.leadForm.controls['foundHome'].setValue('yes');
        break;
      case 'signed_agreement':
        this.leadForm.controls['daysFromBuying'].setValue('yes');
        this.leadForm.controls['foundHome'].setValue('yes');
        this.leadForm.controls['signedAgreement'].setValue('yes');
        break;
    }
  }

  initPrompt(step) {
    this.promptStep = step;
    console.log(this.promptStep);
    this.promptDiplayedLender = this.availableSelected.lenders[this.promptStep - 1];

    // this.availableSelected.lenders[this.promptStep - 1].prompt_checked = true;
    this.availableSelected.lenders[this.promptStep - 1].prompt_displayed = true;
    this.availableSelected.lenders[this.promptStep - 1].prompt_active = true;

  }

  continueWith(answer = 'no') {
    this.availableSelected.lenders[this.promptStep - 1].checked = answer == 'yes' ? true : false;
    this.availableSelected.lenders[this.promptStep - 1].prompt_checked = answer == 'yes' ? true : false;

    // Update form values based on lender object
    const lenderArray: FormArray = this.leadForm.get('lenders') as FormArray;
    const lenderArrayPrompt: FormArray = this.leadForm.get('lenders_prompt') as FormArray;
    const lenderList: FormArray = this.leadForm.get('lendersList') as FormArray;

    // Clean FormArrays
    lenderArray.clear();
    lenderArrayPrompt.clear();
    lenderList.clear();

    this.availableSelected.lenders.forEach(value => {
      if (value.checked) {
        lenderArray.push(new UntypedFormControl(value?.id));
        lenderArrayPrompt.push(new UntypedFormControl(value?.id));
      }
      lenderList.push(new UntypedFormControl(value));
    });

    setTimeout(() => {
      this.availableSelected.lenders[this.promptStep - 1].prompt_active = false;
      if (this.promptStep < this.availableSelected.max_prompt) {
        this.initPrompt(this.promptStep + 1);
        return;
      } else {
        this.isPromptFinished = true;
        this.submitForm();
      }
    }, 400);
  }

  onCheckChange(event) {
    // this.snap();
    // this.jornayaService.snap(); // need check it

    let lenderUp = [];
    const lenderList: FormArray = this.leadForm.get('lendersList') as FormArray;
    lenderList.controls.forEach((ctrl: UntypedFormControl) => {
      if (event.target.value == ctrl.value.id) {
        if (event.target.checked) {
          ctrl.value.checked = true;
        } else {
          ctrl.value.checked = false;
        }
      }
      lenderUp.push(ctrl.value);
    });

    while (lenderList.length !== 0) {
      lenderList.removeAt(0)
    }

    lenderUp.forEach((lender) => {
      lenderList.push(new UntypedFormControl(lender));
    });

    let i: number = 0;
    const lenderArray: FormArray = this.leadForm.get('lenders') as FormArray;
    if(event.target.checked){
      lenderArray.push(new UntypedFormControl(event.target.value));
    } else {
      let i: number = 0;
      lenderArray.controls.forEach((ctrl: UntypedFormControl) => {
        if(ctrl.value == event.target.value) {
          lenderArray.removeAt(i);
          return;
        }

        i++;
      });
    }
  }

  updateCheckboxes(is_checked, value) {
    let lenderUp = [];
    const lenderList: FormArray = this.leadForm.get('lendersList') as FormArray;
    lenderList.controls.forEach((ctrl: UntypedFormControl) => {
      if (value == ctrl.value.id) {
        if (is_checked) {
          ctrl.value.checked = true;
        } else {
          ctrl.value.checked = false;
        }
      }
      lenderUp.push(ctrl.value);
    });

    while (lenderList.length !== 0) {
      lenderList.removeAt(0)
    }

    lenderUp.forEach((lender) => {
      lenderList.push(new UntypedFormControl(lender));
    });

    let i: number = 0;
    const lenderArray: FormArray = this.leadForm.get('lenders') as FormArray;
    if (is_checked){
      lenderArray.push(new UntypedFormControl(value));
    } else {
      let i: number = 0;
      lenderArray.controls.forEach((ctrl: UntypedFormControl) => {
        if(ctrl.value == value) {
          lenderArray.removeAt(i);
          return;
        }

        i++;
      });
    }
  }

  dismissNotification() {
    this.notifications = [];
    return false;
  }
}
